// #region Imports
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@mui/material";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Grow from "@mui/material/Grow";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { useInfiniteScroll } from "_shared/_hooks/infinite-scroll/use-infinite-scroll";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { useImagePreviewDialog } from "_shared/_providers/dialogs/image-preview/image-preview-dialog";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import downloadFile from "_shared/_utilities/download-file";
import * as Config from "config";
import Individual from "marketplace/individuals/individual";
import { messagingApi } from "messaging/_api/messaging-api";
import { individualsApi } from "marketplace/individuals/_api/individual-api";
import MessageQuery from "messaging/_api/queries/message-query";
import MessageParameters, { MessageFilterParameters } from "messaging/_api/queries/message-parameters";
import {
  getIsLoadingMessagesByForumId,
  getMessagesByForumId,
} from "messaging/_stores/messages-redux-slice";
import MessageEditor from "messaging/_views/components/editor/message-editor";
import SearchBar from "messaging/_views/components/message-search-bar";
import { ReceivedMessage } from "messaging/_views/components/received-message-container";
import { SentMessage } from "messaging/_views/components/sent-message-container";
import AttachmentParameters, {
  AttachmentFilterParameters,
} from "messaging/attachments/_api/queries/attachment-parameters";
import AttachmentQuery from "messaging/attachments/_api/queries/attachment-query";
import {
  getAttachmentsByForumId,
  getIsLoadingAttachmentsByForumId,
  populateForumAttachments,
} from "messaging/attachments/_stores/message-attachments-redux-slice";
import AttachmentListItem from "messaging/attachments/_views/message-attachment-list-item";
import MessageFileInfo from "messaging/attachments/file-info/message-file-info";
import { getForumChatTitle, getForumChatTypeLabel } from "messaging/forums/forum-chat-display";
import ForumChatLoadingSkeleton from "messaging/forums/_views/components/forum-chat-loading-skeleton";
import TimelineOverlay from "messaging/forums/_views/components/forum-timeline-overlay";
import Forum from "messaging/forums/forum";
import SubscribersComponent from "messaging/forums/subscribers/_views/subscriber-list";
import AddSubscriber from "messaging/forums/subscribers/invitations/_views/invite-forum-subscriber-view";
import Message from "messaging/message";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useSession } from "users/__session/session-context";
// #endregion

// #region Styled Components
const MessageLoadingIndicator = styled(CircularProgress)(({ theme }) => ({
  color: theme.palette.primary.main,
  height: "32px",
  margin: theme.spacing(2),
  width: "32px",
}));
const TopLoadingIndicatorContainer = styled("div")(() => ({
  backgroundColor: "#f8f9fd",
  display: "flex",
  justifyContent: "center",
  paddingBottom: "8px",
  position: "sticky",
  top: "48px",
  width: "100%",
}));
const OuterContainer = styled("div", {
  shouldForwardProp: (prop) => prop !== "fillAvailableSpace",
})<{ fillAvailableSpace: boolean }>(({ theme, fillAvailableSpace }) => ({
  display: "grid",
  flex: fillAvailableSpace ? "1 1 auto" : undefined,
  gridTemplateRows: "auto minmax(0, 1fr) auto",
  height: fillAvailableSpace ? "100%" : `calc(100% - ${theme.spacing(2)})`,
  minHeight: fillAvailableSpace ? 0 : "75vh",
  minWidth: 0,
  overflow: fillAvailableSpace ? "hidden" : undefined,
}));
const ForumTabs = styled(Tabs)(() => ({
  gridRowStart: 1,
  "& .MuiTabs-flexContainer": {
    borderBottom: `1px solid #aaa`,
    height: "100%",
  },
  "& .MuiTab-root": {
    "&:not(:first-of-type)": {
      borderLeft: 0,
    },
    maxWidth: 'unset !important'
  },
  "& .MuiTabs-indicator": {
    display: 'none'
  }
}));
const ForumTab = styled(Tab)(() => ({
  backgroundColor: "#f8f9fd",
  borderTopLeftRadius: "4px",
  borderTopRightRadius: "4px",
  flex: 1,
  border: `1px solid #aaa`,
  "&.Mui-selected": {
    borderBottom: "none",
  },
  "&:not(.Mui-selected)": {
    backgroundColor: "#e8e9ef",
  },
}));
const TabLabelContainer = styled("div")(() => ({
  fontSize: "0.85em",
  justifyItems: "space-between",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  width: "100%",
}));
const ForumTabLabel = styled("div")(() => ({
  alignItems: "center",
  display: "flex",
  gap: "8px",
  justifyContent: "space-between",
  width: "100%",
}));
const ForumTabTitle = styled("span")(() => ({
  flex: "1 1 auto",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
const ForumTabTitleMeasure = styled("span")(() => ({
  position: "absolute",
  visibility: "hidden",
  whiteSpace: "nowrap",
  width: "max-content",
}));
const ChatTypeChip = styled(Chip)(({ theme }) => ({
  flexShrink: 0,
  fontSize: theme.typography.caption.fontSize,
  height: theme.spacing(2.5),
  maxWidth: "45%",
  "& .MuiChip-label": {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
}));
const CloseButtonContainer = styled("span")(() => ({
  display: "inline-flex",
  flexShrink: 0,
}));
const CloseButton = styled(CloseIcon)(({ theme }) => ({
  color: theme.palette.text.secondary,
  cursor: "pointer",
  marginLeft: theme.spacing(1),
  "&:hover": {
    color: theme.palette.primary.dark,
  },
}));
const MessagePane = styled("div", {
  shouldForwardProp: (prop) => prop !== "hideScrollbar",
})<{ hideScrollbar: boolean }>(({ hideScrollbar }) => ({
  containerType: "inline-size",
  minHeight: 0,
  overflowY: hideScrollbar ? "hidden" : "auto",
  position: "relative",
  scrollbarGutter: "stable",
  width: "100%",
}));
const MessageContainer = styled("div", {
  shouldForwardProp: (prop) => prop !== "formattingEnabled",
})<{ formattingEnabled: boolean }>(({ formattingEnabled, theme }) => ({
  backgroundColor: "#f8f9fd",
  border: "1px solid #aaa",
  borderBottomLeftRadius: formattingEnabled ? 0 : theme.shape.borderRadius,
  borderBottomRightRadius: formattingEnabled ? 0 : theme.shape.borderRadius,
  borderTop: "none",
  display: "inline-grid",
  gridRowStart: 2,
  gridTemplateRows: "1fr auto",
  minHeight: 0,
  paddingLeft: theme.spacing(2),
  position: "relative",
  width: "100%",
}));
const EmptyMessage = styled(Typography)(() => ({
  alignContent: "center",
  alignItems: "center",
  color: "rgba(0, 0, 0, 0.5)",
  display: "flex",
  fontSize: "1.33em",
  height: "100%",
  justifyContent: "center",
  textAlign: "center",
  whiteSpace: "normal",
  width: "100%",
}));
const BackToTopChipContainer = styled("div")(() => ({
  display: "flex",
  height: 0,
  justifyContent: "center",
  position: "relative",
  width: "100%",
  zIndex: 1,
}));
const BackToTopChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  bottom: "20px",
  boxShadow: "0 4px 5px 0 rgb(0 0 0 / 14%), 0 1px 10px 0 rgb(0 0 0 / 12%), 0 2px 4px 0 rgb(0 0 0 / 20%)",
  color: "white",
  position: "absolute",
  "&:hover": {
    backgroundColor: theme.palette.primary.main,
  },
}));
const EditorContainer = styled("div")(() => ({
  gridRowStart: 3,
  width: "100%",
}));
const SearchField = styled(SearchBar)(() => ({
  backdropFilter: "blur(10px) brightness(105%)",
  backgroundColor: "rgba(255, 255, 255, 0.82)",
  "&:before": {
    backgroundColor: "#f8f9fd",
    content: "''",
    height: "8px",
  },
}));
// #endregion

export type UserAvatarMap = {
  [userId: string]: string;
};

type ChatWindowProps = {
  className?: string;
  fillAvailableSpace?: boolean;
  forums: Forum[];
  locked?: boolean;
  lockedMessage?: string;
  readOnly?: boolean;
  onClose?: () => void;
  onLeaveConversation?: () => void;
};

type ActiveForumSubscriberData = {
  forumId?: string;
  subscribers: Individual[];
  avatars: UserAvatarMap;
};

/**
 * These two live outside the component on purpose. Both need a `finally` — one to unsubscribe
 * the request, one to clear the loading flag — and the React Compiler cannot lower a `finally`,
 * so leaving them inline made it skip memoizing the whole chat window. Module-level functions
 * are not compiled, so the original control flow is preserved exactly.
 */
async function loadForumSubscribers(
  forum: Forum,
  abortController: AbortController,
  dispatch: ReturnType<typeof useAttorneyHubDispatch>,
  fetchUserAvatars: (subscribers: Individual[]) => Promise<UserAvatarMap>,
  setActiveForumSubscriberData: (data: ActiveForumSubscriberData) => void,
): Promise<void> {
  if (!forum.id) return;
  // A superseded load is discarded by the `aborted` checks below rather than cancelled, because
  // `initiate` hands back a handle on a cache entry shared by everyone asking for this forum.
  // Aborting it writes the abort error into that shared entry, and the load superseding this one
  // re-initiates the same entry in the same tick — RTK Query sees the first request still pending,
  // skips the second thunk, and settles it from the entry the abort has just failed. The window
  // then reports "Failed to get subscribers" for a request nothing cancelled, and no later load
  // re-requests it. Letting the request finish instead fills the entry, so the load that
  // superseded this one reads the subscribers straight out of it.
  const request = dispatch(
    messagingApi.endpoints.getForumSubscriberInfo.initiate({
      forumId: forum.id.value,
    })
  );

  try {
    const subscribers = await request.unwrap();
    const userAvatars = await fetchUserAvatars(subscribers);
    if (abortController.signal.aborted) return;

    setActiveForumSubscriberData({
      forumId: forum.id?.value,
      subscribers,
      avatars: userAvatars,
    });
  } catch (err) {
    if (abortController.signal.aborted) return;
    console.error(err);
    enqueueSnackbar("Failed to get subscribers", { variant: "error" });
  } finally {
    request.unsubscribe();
  }
}

async function loadForumSubscribersAndAvatars(
  forum: Forum,
  abortController: AbortController,
  loadSubscribers: (forum: Forum, abortController: AbortController) => Promise<void>,
  setIsLoadingForum: (isLoading: boolean) => void,
): Promise<void> {
  setIsLoadingForum(true);
  try {
    await loadSubscribers(forum, abortController);
  } finally {
    if (!abortController.signal.aborted) {
      setIsLoadingForum(false);
    }
  }
}

type ChatTabLabelProps = {
  forum: Forum;
  currentUserName?: string;
  onClose?: () => void;
};

function ChatTabLabel(props: Readonly<ChatTabLabelProps>) {
  const { forum, currentUserName, onClose } = props;
  const labelRef = useRef<HTMLDivElement>(null);
  const titleMeasureRef = useRef<HTMLSpanElement>(null);
  const chipRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLSpanElement>(null);
  const chipWidthRef = useRef<number | undefined>(undefined);
  const [showChatType, setShowChatType] = useState(true);
  const title = getForumChatTitle(forum, currentUserName) || "Messages";
  const chatType = getForumChatTypeLabel(forum);

  useEffect(() => {
    const label = labelRef.current;
    if (!label || typeof ResizeObserver === "undefined") return;

    const updateChatTypeVisibility = () => {
      const titleWidth = titleMeasureRef.current?.getBoundingClientRect().width ?? 0;
      const chipWidth = chipWidthRef.current ?? chipRef.current?.getBoundingClientRect().width ?? 0;
      if (!label.clientWidth || !titleWidth || !chipWidth) return;

      const gap = 8;
      const closeWidth = closeRef.current?.getBoundingClientRect().width ?? 0;
      const requiredWidth = titleWidth + chipWidth + gap + (onClose ? closeWidth + gap : 0);
      setShowChatType(label.clientWidth >= requiredWidth);
    };

    const chipWidth = chipRef.current?.getBoundingClientRect().width;
    if (chipWidth) chipWidthRef.current = chipWidth;

    const observer = new ResizeObserver(updateChatTypeVisibility);
    observer.observe(label);
    updateChatTypeVisibility();

    return () => observer.disconnect();
  }, [forum, onClose, title]);

  return (
    <TabLabelContainer ref={labelRef}>
      <ForumTabLabel>
        <ForumTabTitle>{title}</ForumTabTitle>
        <ChatTypeChip
          ref={chipRef}
          label={chatType}
          size="small"
          style={{ display: showChatType ? undefined : "none" }}
          variant="outlined"
        />
        {onClose && (
          <CloseButtonContainer ref={closeRef}>
            <Tooltip title="Close Chat">
              <CloseButton
                onClick={(event) => {
                  event.stopPropagation();
                  onClose();
                }}
              />
            </Tooltip>
          </CloseButtonContainer>
        )}
      </ForumTabLabel>
      <ForumTabTitleMeasure aria-hidden="true" ref={titleMeasureRef}>
        {title}
      </ForumTabTitleMeasure>
    </TabLabelContainer>
  );
}

export default function ChatWindow(props: Readonly<ChatWindowProps>) {
  const { className, forums, locked, lockedMessage, readOnly, onClose, onLeaveConversation } = props;
  const fillAvailableSpace = props.fillAvailableSpace ?? false;

  // #region Refs
  const bottomRef = useRef<HTMLDivElement>(null);
  const messagePaneRef = useRef<HTMLDivElement>(null);

  const visibleForums: Forum[] = useMemo(() => {
    if (readOnly) {
      return forums?.filter((forum) => (forum?.subscribers?.length ?? 0) > 1) ?? [];
    }
    return forums?.filter((forum) => forum.id) ?? [];
  }, [forums, readOnly]);

  // #region State
  const [activeForumId, setActiveForumId] = useState<string | undefined>();
  const [activeForumSubscriberData, setActiveForumSubscriberData] = useState<ActiveForumSubscriberData>({
    subscribers: [],
    avatars: {},
  });
  const [searchString, setSearchString] = useState<string | undefined>();
  const [minCreatedDate, setMinCreatedDate] = useState<string | undefined>();
  const [maxCreatedDate, setMaxCreatedDate] = useState<string | undefined>();
  const [initialLoadCompleted, setInitialLoadCompleted] = useState(false);
  const [isLoadingForum, setIsLoadingForum] = useState(false);
  const [backToTopVisible, setBackToTopVisible] = useState(false);
  const [showSystemMessages, setShowSystemMessages] = useState(false);
  const [showAttachments, setShowAttachments] = useState(false);
  const [filterDates, setFilterDates] = useState(false);
  const [timelineActive, setTimelineActive] = useState(false);
  const [formattingEnabled, setFormattingEnabled] = useState(false);
  const activeForum = useMemo(() => {
    return visibleForums.find((forum) => forum.id?.value === activeForumId) ?? visibleForums[0];
  }, [activeForumId, visibleForums]);
  const activeForumSubscribers = useMemo(
    () =>
      activeForumSubscriberData.forumId === activeForum?.id?.value
        ? activeForumSubscriberData.subscribers
        : [],
    [activeForum?.id?.value, activeForumSubscriberData],
  );
  const activeForumAvatars =
    activeForumSubscriberData.forumId === activeForum?.id?.value ? activeForumSubscriberData.avatars : {};
  const listedForumSubscribers = useMemo(
    () =>
      activeForumSubscribers.filter((individual) =>
        activeForum?.subscribers?.some((subscriber) => subscriber.userId.isEqualTo(individual.userId)),
      ),
    [activeForum?.subscribers, activeForumSubscribers],
  );

  // #region Selectors
  const attachments = useSelector(getAttachmentsByForumId(activeForum?.id));
  const isLoadingAttachments = useSelector(getIsLoadingAttachmentsByForumId(activeForum?.id));
  const messages = useSelector(getMessagesByForumId(activeForum?.id));
  const isLoadingMessages = useSelector(getIsLoadingMessagesByForumId(activeForum?.id));
  const latestMessageId = messages.data?.at(-1)?.id?.value;
  const hideMessagePaneScrollbar = !showAttachments && messages.isEmpty;

  /**
   * The two ways this pane waits, told apart by whether it has anything to show yet.
   *
   * They used to be three independent booleans read straight into three sibling conditions, and on
   * a first open all three were true: the pane opened with a sticky spinner at the top, a second
   * differently-sized one captioned "Loading..." in the middle, and nothing else. Deriving the two
   * states from the same pair of facts is what stops them co-occurring -- the skeleton renders only
   * while the list is empty, and the top spinner only while it is not, so neither can appear over
   * the other however the three fetches happen to interleave.
   */
  const activeListIsEmpty = showAttachments ? attachments.isEmpty : messages.isEmpty;
  const isLoadingActiveList = showAttachments ? isLoadingAttachments : isLoadingMessages;
  /**
   * Nothing on screen, and something on its way to put something there.
   *
   * `isLoadingForum` belongs here rather than beside the list check because the content below is
   * withheld while it is true whatever the list holds -- so a forum still resolving its subscribers
   * leaves the pane blank even when messages have already arrived by realtime, which is precisely
   * the case that put a lone spinner over an empty pane.
   */
  const isFillingEmptyPane = isLoadingForum || (activeListIsEmpty && isLoadingActiveList);
  /**
   * A further page, over content the reader is already looking at.
   *
   * Defined against the state above rather than alongside it, so the two cannot both be true by
   * construction however the three fetches interleave.
   */
  const isLoadingOlderPage = !isFillingEmptyPane && isLoadingActiveList;

  // #region Hooks
  const { openDialog, closeDialog } = useDialog();
  const { openImagePreviewDialog } = useImagePreviewDialog();
  const session = useSession();
  const isActiveForumCreator = activeForum?.creator?.userId.isEqualTo(session.user?.id) ?? false;
  const dispatch = useAttorneyHubDispatch();
  useInfiniteScroll(
    // Hook for infinite message loading
    fetchMoreMessages,
    messages.hasNextPage,
    isLoadingMessages,
    messagePaneRef,
    true
  );
  useInfiniteScroll(
    // Hook for infinite attachment loading
    fetchMoreAttachments,
    attachments.hasNextPage,
    isLoadingForum || isLoadingAttachments,
    messagePaneRef,
    true
  );

  const fetchUserAvatars = useCallback(
    async (
      subscribers: Individual[]
    ): Promise<UserAvatarMap> => {
      let userAvatars: UserAvatarMap = {};
      for (let subscriber of subscribers) {
        if (!subscriber.profile?.avatarId) continue;
        if (userAvatars[subscriber.id.value] && userAvatars[subscriber.userId.value]) continue;

        try {
          const avatarData = await dispatch(
            individualsApi.endpoints.getIndividualAvatar.initiate({ individualId: subscriber.id.value, size: "small" }),
          ).unwrap();
          if (avatarData) {
            userAvatars[subscriber.id.value] = avatarData;
            userAvatars[subscriber.userId.value] = avatarData;
          }
        } catch {
          // Avatar failures are non-blocking. Keep the failure private and continue loading the chat.
        }
      }
      return userAvatars;
    },
    [dispatch]
  );

  const loadSubscribers = useCallback(
    (forum: Forum, abortController: AbortController) =>
      loadForumSubscribers(forum, abortController, dispatch, fetchUserAvatars, setActiveForumSubscriberData),
    [dispatch, fetchUserAvatars]
  );


  const handleWindowScrolled = useCallback(() => {
    const pane = messagePaneRef.current;
    if (!pane) return;

    const { scrollTop, scrollHeight, clientHeight } = pane;
    const maxScroll = scrollHeight - clientHeight;

    // Hide the back to top button if there's nothing to scroll
    if (maxScroll <= 0) {
      setBackToTopVisible(false);
      return;
    }

    // Otherwise, show the button if we’re at the bottom
    const scrollPercent = Math.round((scrollTop / maxScroll) * 100);
    setBackToTopVisible(scrollPercent < 99);

    // If we are at the bottom, set initialLoadCompleted to true
    if (!initialLoadCompleted && scrollPercent === 100) {
      setInitialLoadCompleted(true);
    }
  }, [initialLoadCompleted]);

  const scrollToBottom = useCallback(
    (delayMs: number = 0) => {
      if (!bottomRef.current) return;
      setTimeout(() => {
        bottomRef?.current?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "nearest",
        });
      }, delayMs);
    },
    [bottomRef]
  );

  const loadActiveForumSubscribersAndAvatars = useCallback(
    (forum: Forum, abortController: AbortController) =>
      loadForumSubscribersAndAvatars(forum, abortController, loadSubscribers, setIsLoadingForum),
    [loadSubscribers]
  );

  const populateAttachments = useCallback(() => {
    if (!activeForum) return;

    const query = new AttachmentQuery(
      new AttachmentParameters(new AttachmentFilterParameters(searchString, minCreatedDate, maxCreatedDate)),
      PaginationParameters.create({
        pageIndex: 0,
        pageSize: Config.chatPageSize,
      })
    );
    dispatch(
      populateForumAttachments({
        session,
        forum: activeForum,
        query,
        append: !searchString && !showSystemMessages && !filterDates,
      })
    );
  }, [activeForum, dispatch, filterDates, maxCreatedDate, minCreatedDate, searchString, session, showSystemMessages]);

  const populateMessages = useCallback(() => {
    if (!activeForum?.id) return;

    const query = new MessageQuery(
      new MessageParameters(
        new MessageFilterParameters({
          searchString,
          systemOnly: showSystemMessages,
          minCreatedDate,
          maxCreatedDate,
        })
      ),
      PaginationParameters.create({
        pageIndex: 0,
        pageSize: Config.chatPageSize,
      })
    );
    return dispatch(
      messagingApi.endpoints.getMessagesByForum.initiate({
        forumId: activeForum.id.value,
        query,
        append: !searchString && !showSystemMessages && !filterDates,
      }, { forceRefetch: true })
    );
  }, [activeForum, dispatch, filterDates, maxCreatedDate, minCreatedDate, searchString, showSystemMessages]);

  // #region Lifecycle
  // 1. DOM Event Listeners (Runs once on mount)
  useEffect(() => {
    const pane = messagePaneRef.current;

    const handlePointerDown = (e: PointerEvent) => {
      if (!pane) return;
      const { right } = pane.getBoundingClientRect();
      const scrollbarWidth = 16;
      if (e.clientX >= right - scrollbarWidth && e.clientX <= right) {
        setTimelineActive(true);
      }
    };

    const timeoutId = globalThis.setTimeout(() => {
      pane?.addEventListener("scroll", handleWindowScrolled, { passive: true });
      pane?.addEventListener("pointerdown", handlePointerDown as EventListener, true);
    }, 500);

    return () => {
      clearTimeout(timeoutId);
      pane?.removeEventListener("scroll", handleWindowScrolled);
      pane?.removeEventListener("pointerdown", handlePointerDown as EventListener, true);
    };
  }, [handleWindowScrolled]);

  // 2. Load subscribers for the active forum
  useEffect(() => {
    if (!activeForum) return

    const abortController = new AbortController();
    let active = true;
    queueMicrotask(() => {
      if (!active) return;
      void loadActiveForumSubscribersAndAvatars(activeForum, abortController);
    });

    return () => {
      active = false;
      abortController.abort();
    };
  }, [activeForum, loadActiveForumSubscribersAndAvatars]);

  // 4. Scroll to bottom on message load
  useEffect(() => {
    if (isLoadingMessages) return;
    scrollToBottom();
  }, [isLoadingMessages, scrollToBottom, showAttachments, showSystemMessages]);

  useEffect(() => {
    if (showAttachments || !latestMessageId) return;
    scrollToBottom();
  }, [latestMessageId, scrollToBottom, showAttachments]);

  useEffect(() => {
    if (!activeForum) return;

    if (showAttachments) {
      populateAttachments();
    } else {
      populateMessages();
    }
  }, [activeForum, populateAttachments, populateMessages, showAttachments]);
  // #endregion

  // #region Functions
  function fetchMoreMessages() {
    if (!activeForum?.id || !messages.hasNextPage || isLoadingForum || !initialLoadCompleted) return;

    const query = new MessageQuery(
      new MessageParameters(
        new MessageFilterParameters({
          searchString,
          systemOnly: showSystemMessages,
          minCreatedDate,
          maxCreatedDate,
        })
      ),
      PaginationParameters.create({
        pageIndex: messages.pageIndex + 1,
        pageSize: messages.pageSize,
      })
    );
    dispatch(
      messagingApi.endpoints.getMessagesByForum.initiate({
        forumId: activeForum.id.value,
        query,
        append: true,
      }, { subscribe: false })
    );

    const pane = messagePaneRef.current;
    if (!pane) return;

    const prevHeight = pane.scrollHeight;

    // Prevent the infinite loading from triggering more than once
    requestAnimationFrame(() => {
      const newHeight = pane.scrollHeight;
      pane.scrollTop = newHeight - prevHeight;
    });
  }

  function fetchMoreAttachments() {
    if (!activeForum || !attachments.hasNextPage || isLoadingForum || !initialLoadCompleted) return;

    const query = new AttachmentQuery(
      new AttachmentParameters(new AttachmentFilterParameters(searchString, minCreatedDate, maxCreatedDate)),
      PaginationParameters.create({
        pageIndex: attachments.pageIndex + 1,
        pageSize: attachments.pageSize,
      })
    );
    dispatch(
      populateForumAttachments({
        session,
        forum: activeForum,
        query,
        append: true,
      })
    );

    const pane = messagePaneRef.current;
    if (!pane) return;

    const prevHeight = pane.scrollHeight;

    // Prevent the infinite loading from triggering more than once
    requestAnimationFrame(() => {
      const newHeight = pane.scrollHeight;
      pane.scrollTop = newHeight - prevHeight;
    });
  }

  function handleTimelineDateSelected(startDate?: string, endDate?: string) {
    if (!startDate || !endDate) {
      setTimelineActive(false);
      return;
    }
    setFilterDates(true);
    setMinCreatedDate(startDate);
    setMaxCreatedDate(endDate);
  }

  function handleTimelineReset() {
    setMinCreatedDate(undefined);
    setMaxCreatedDate(undefined);
    setFilterDates(false);
  }

  function handleAddSubscriberPressed(): void {
    if (!activeForum) return;
    const component = <AddSubscriber forum={activeForum} onClosed={closeDialog} />;
    openDialog({ component: component, title: "Add People to Chat" });
  }

  function handleLeaveConversationPressed(): void {
    onLeaveConversation?.();
    closeDialog();
  }

  function handleToggleAttachments() {
    if (!showAttachments) {
      populateAttachments();
    }
    setInitialLoadCompleted(false);
    setSearchString(undefined);
    setMinCreatedDate(undefined);
    setMaxCreatedDate(undefined);
    setShowAttachments(!showAttachments);
  }

  function handleToggleSystemMessages() {
    setInitialLoadCompleted(false);
    setSearchString(undefined);
    setMinCreatedDate(undefined);
    setMaxCreatedDate(undefined);
    setShowSystemMessages(!showSystemMessages);
  }

  function handleToggleFilterDates(): void {
    if (filterDates) {
      setMinCreatedDate(undefined);
      setMaxCreatedDate(undefined);
      setTimelineActive(false);
      setFilterDates(false);
    } else {
      setTimelineActive(true);
    }
  }

  const handleSearch = useCallback((searchString: string): void => {
    setSearchString(searchString);
    if (searchString === "") {
      scrollToBottom(1000);
    }
  }, [scrollToBottom]);

  function getEmptyMessageString(): string {
    if (locked) {
      return lockedMessage ?? "Chat was locked by the moderator";
    }
    if (searchString) {
      return "No Matching Results";
    }
    return "No Messages";
  }

  // Declared before `openImagePreview`, which closes over it: the React Compiler cannot rewrite a
  // reference to a function that is only hoisted into scope, and skips the component when it finds one.
  async function downloadAttachment(message: Message, attachment: MessageFileInfo): Promise<void> {
    // Handled up front rather than thrown into the catch below: the React Compiler cannot lower a
    // `throw` inside a try block, and the outcome for the user is identical.
    const forumId = message.forum.id;
    if (!forumId) {
      enqueueSnackbar("Failed to get message attachment. Please try again.", { variant: "error" });
      return;
    }

    try {
      const request = messagingApi.endpoints.downloadMessageAttachment.initiate({
          forumId: forumId.value,
          fileId: attachment.id.value,
        },
        { subscribe: false }
      );
      const runningRequest = dispatch(request);
      const response = await runningRequest.unwrap();
      await downloadFile(response, attachment.name);
    } catch {
      enqueueSnackbar("Failed to get message attachment. Please try again.", { variant: "error" });
    }
  }

  function openImagePreview(message: Message, image: MessageFileInfo) {
    if (!image.dataUri) {
      enqueueSnackbar("Image not found", { variant: "error" });
      return;
    }
    onClose?.();
    openImagePreviewDialog(
      {
        name: image.name,
        src: image.dataUri,
      },
      () => downloadAttachment(message, image)
    );
  }
  // #endregion

  // #region Render
  return (
    <OuterContainer
      className={className}
      data-chat-layout={fillAvailableSpace ? "fill" : undefined}
      fillAvailableSpace={fillAvailableSpace}
    >
      <ForumTabs
        value={activeForum?.id?.value ?? false}
        onChange={(_, newValue) => {
          if (visibleForums.some((forum) => forum.id?.value === newValue)) {
            setActiveForumId(newValue);
          }
        }}
        textColor="primary"
      >
        {visibleForums.length === 0 && <ForumTab disabled label="No Forums Found" value="no-forums" />}
        {visibleForums.map((forum: Forum) => (
          <ForumTab
            key={forum.id?.value}
            disabled={locked || readOnly}
            value={forum.id?.value}
            onClick={() => setActiveForumId(forum.id?.value)}
            style={{ maxWidth: `calc((100% / ${forums.length}) - 48px)` }}
            label={<ChatTabLabel currentUserName={session.user?.fullName} forum={forum} onClose={onClose} />}
          />
        ))}
        <SubscribersComponent
          subscribers={listedForumSubscribers}
          forum={activeForum}
          disableAddButton={!isActiveForumCreator}
          avatars={activeForumAvatars}
          disableLeaveButton={locked || readOnly}
          onAddSubscriber={handleAddSubscriberPressed}
          onLeaveConversation={handleLeaveConversationPressed}
        />
      </ForumTabs>
      <MessageContainer formattingEnabled={formattingEnabled}>
        <MessagePane ref={messagePaneRef} hideScrollbar={hideMessagePaneScrollbar}>
          <SearchField
            showSystemMessages={showSystemMessages}
            showAttachments={showAttachments}
            filterDates={filterDates}
            onToggleDates={handleToggleFilterDates}
            onToggleSystemMessages={handleToggleSystemMessages}
            onToggleAttachments={handleToggleAttachments}
            onSearch={handleSearch}
          />
          {isLoadingOlderPage && (
            <TopLoadingIndicatorContainer>
              <MessageLoadingIndicator disableShrink thickness={4} />
            </TopLoadingIndicatorContainer>
          )}
          {isFillingEmptyPane && (
            <ForumChatLoadingSkeleton variant={showAttachments ? "attachments" : "messages"} />
          )}
          {!isFillingEmptyPane && (
            <>
              {showAttachments && attachments.isEmpty && !isLoadingAttachments && (
                <EmptyMessage>No Attachments</EmptyMessage>
              )}
              {showAttachments && !attachments.isEmpty && (
                <>
                  {attachments.data?.map((attachment: MessageFileInfo) => (
                    <AttachmentListItem
                      key={attachment.id?.value}
                      session={session}
                      forumId={activeForum?.id}
                      attachmentInfo={attachment}
                    />
                  ))}
                </>
              )}
              {!showAttachments && (
                <>
                  {messages.isEmpty && !isLoadingMessages && <EmptyMessage>{getEmptyMessageString()}</EmptyMessage>}
                  {!locked &&
                    messages.data?.map((message: Message) => {
                      if (session.user && message.userIsSender(session.user)) {
                        return (
                          <SentMessage
                            key={message.id?.value}
                            data-date={message.created?.format("MM/DD/YY hh:mm A")}
                            message={message}
                            avatarSrc={activeForumAvatars[message.senderId?.value ?? ""]}
                            onImageLoad={() => scrollToBottom(1000)}
                            onImageClick={openImagePreview}
                            onDownload={downloadAttachment}
                          />
                        );
                      } else
                        return (
                          <ReceivedMessage
                            key={message.id?.value}
                            data-date={message.created?.format("MM/DD/YY hh:mm A")}
                            sender={activeForumSubscribers.find((subscriber: Individual) => {
                              return subscriber.userId?.isEqualTo(message.senderId);
                            })}
                            avatars={activeForumAvatars}
                            message={message}
                            onImageLoad={() => scrollToBottom(1000)}
                            onImageClick={openImagePreview}
                            onDownload={downloadAttachment}
                          />
                        );
                    })}
                  {locked && <EmptyMessage>{lockedMessage ?? "Chat was locked by the moderator"}</EmptyMessage>}
                </>
              )}
            </>
          )}
          <div ref={bottomRef} />
        </MessagePane>
        <TimelineOverlay
          show={timelineActive}
          firstDate={showAttachments ? attachments.minCreatedDate : messages.minCreatedDate}
          lastDate={showAttachments ? attachments.maxCreatedDate : messages.maxCreatedDate}
          onDateSelected={handleTimelineDateSelected}
          onReset={handleTimelineReset}
        />
        {((!isLoadingForum && showAttachments && !attachments.isEmpty) || (!showAttachments && !messages.isEmpty)) && (
          <BackToTopChipContainer>
            <Grow in={backToTopVisible}>
              <BackToTopChip
                label="Jump to bottom"
                icon={<ArrowDownwardIcon style={{ color: "white" }} />}
                onClick={() => scrollToBottom()}
              />
            </Grow>
          </BackToTopChipContainer>
        )}
      </MessageContainer>
      {!readOnly && (
        <EditorContainer>
          <MessageEditor
            forum={activeForum}
            onFormattingToggled={(enabled) => {
              setFormattingEnabled(enabled);
              scrollToBottom(1000);
            }}
            onAttachmentAdded={() => scrollToBottom(1000)}
            onMessageSent={() => scrollToBottom(1000)}
            locked={locked}
          />
        </EditorContainer>
      )}
    </OuterContainer>
  );
  // #endregion
}
